import { useRef } from "react";
import { QrCode, Download } from "lucide-react";
import { toast } from "sonner";
import QRWithLogo from "./QRWithLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";

export default function QRDialog({
  url,
  title,
  label = "QR",
  heading = "Codice QR dell'annuncio",
  description = "Stampalo e appendilo in biblioteca o in bacheca: chi lo inquadra apre subito questo libro.",
}) {
  const wrapRef = useRef(null);

  const download = () => {
    const svg = wrapRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const src = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const size = 640, pad = 48;
      const canvas = document.createElement("canvas");
      canvas.width = size + pad * 2;
      canvas.height = size + pad * 2;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, pad, pad, size, size);
      // logo badge al centro
      const cx = pad + size / 2, cy = pad + size / 2, b = size * 0.22;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#e7e0d0";
      ctx.lineWidth = 3;
      const r = 10, x = cx - b / 2, y = cy - b / 2;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + b, y, x + b, y + b, r);
      ctx.arcTo(x + b, y + b, x, y + b, r);
      ctx.arcTo(x, y + b, x, y, r);
      ctx.arcTo(x, y, x + b, y, r);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // libro stilizzato
      ctx.strokeStyle = "#8C3A27";
      ctx.lineWidth = Math.max(3, b * 0.06);
      const bw = b * 0.5, bh = b * 0.4;
      ctx.strokeRect(cx - bw / 2, cy - bh / 2, bw, bh);
      ctx.beginPath();
      ctx.moveTo(cx, cy - bh / 2);
      ctx.lineTo(cx, cy + bh / 2);
      ctx.stroke();
      URL.revokeObjectURL(src);
      const a = document.createElement("a");
      a.download = `qr-${(title || "libri-in-circolo").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success("QR scaricato");
    };
    img.onerror = () => toast.error("Impossibile generare il QR");
    img.src = src;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="qr-button"><QrCode className="w-4 h-4 mr-2" />{label}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-serif">{heading}</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>
        <div ref={wrapRef} data-testid="qr-code" className="mx-auto my-2 p-5 bg-white rounded-xl border border-border">
          <QRWithLogo value={url} size={200} />
        </div>
        <Button onClick={download} data-testid="qr-download" className="bg-primary hover:bg-primary/90"><Download className="w-4 h-4 mr-2" />Scarica PNG</Button>
      </DialogContent>
    </Dialog>
  );
}
